import { createWorker } from 'tesseract.js';
import { z } from 'zod';
import {
  type PhilippineIdType,
  PHILIPPINE_IDS,
  PH_OCR_PATTERNS,
  extractPhilippineIdData,
  validatePhilippineIdData,
  validatePhilippineIdFormat,
} from './philippineIds';

// Document types supported
export type DocumentType =
  | 'passport'
  | 'driverLicense'
  // Philippine IDs
  | 'philid'
  | 'umid'
  | 'ph_drivers_license'
  | 'ph_passport'
  | 'sss'
  | 'tin'
  | 'philhealth'
  | 'voters_id'
  | 'prc'
  | 'postal_id';

// Validation schemas for different ID types
const passportSchema = z.object({
  documentNumber: z.string().regex(/^[A-Z0-9]{8,9}$/),
  surname: z.string().min(1),
  givenNames: z.string().min(1),
  dateOfBirth: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  expiryDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  nationality: z.string().min(2),
});

const driverLicenseSchema = z.object({
  licenseNumber: z.string().min(5),
  surname: z.string().min(1),
  givenNames: z.string().min(1),
  dateOfBirth: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  expiryDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  state: z.string().min(2),
});

// Check if document type is a Philippine ID
export function isPhilippineId(documentType: DocumentType): boolean {
  return [
    'philid', 'umid', 'ph_drivers_license', 'ph_passport',
    'sss', 'tin', 'philhealth', 'voters_id', 'prc', 'postal_id'
  ].includes(documentType);
}

// Map document type to Philippine ID type
function mapToPhilippineIdType(documentType: DocumentType): PhilippineIdType | null {
  const mapping: Record<string, PhilippineIdType> = {
    'philid': 'philid',
    'umid': 'umid',
    'ph_drivers_license': 'drivers_license',
    'ph_passport': 'passport',
    'sss': 'sss',
    'tin': 'tin',
    'philhealth': 'philhealth',
    'voters_id': 'voters_id',
    'prc': 'prc',
    'postal_id': 'postal_id',
  };
  return mapping[documentType] || null;
}

export async function extractTextFromImage(image: File): Promise<string> {
  const worker = await createWorker('eng');

  // Read the image file
  const imageData = await readFileAsDataURL(image);

  // Perform OCR
  const { data: { text } } = await worker.recognize(imageData);

  await worker.terminate();
  return text;
}

async function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function validateDocument(text: string, documentType: DocumentType) {
  // Check if this is a Philippine ID
  if (isPhilippineId(documentType)) {
    return validatePhilippineDocument(text, documentType);
  }

  // Extract data using regex patterns
  const data = extractDocumentData(text, documentType as 'passport' | 'driverLicense');

  try {
    // Validate extracted data against schema
    if (documentType === 'passport') {
      return passportSchema.parse(data);
    } else {
      return driverLicenseSchema.parse(data);
    }
  } catch (error) {
    throw new Error('Invalid document data');
  }
}

// Validate Philippine document
async function validatePhilippineDocument(text: string, documentType: DocumentType) {
  const phIdType = mapToPhilippineIdType(documentType);
  if (!phIdType) {
    throw new Error('Invalid Philippine document type');
  }

  // Extract data using Philippine OCR patterns
  const data = extractPhilippineIdData(text, phIdType);

  // Validate extracted data
  const validation = validatePhilippineIdData(data, phIdType);

  if (!validation.isValid) {
    throw new Error(`Invalid Philippine ID data: ${validation.errors.join(', ')}`);
  }

  return data;
}

// Detect document type from OCR text
export function detectDocumentType(text: string): DocumentType | null {
  const normalizedText = text.toUpperCase();

  // Philippine ID detection patterns
  if (normalizedText.includes('REPUBLIKA NG PILIPINAS') || normalizedText.includes('PHILIPPINE IDENTIFICATION')) {
    if (normalizedText.includes('PSN') || normalizedText.includes('PHILSYS')) {
      return 'philid';
    }
  }

  if (normalizedText.includes('UNIFIED MULTI-PURPOSE') || normalizedText.includes('UMID')) {
    return 'umid';
  }

  if (normalizedText.includes('LAND TRANSPORTATION OFFICE') || normalizedText.includes('LTO')) {
    return 'ph_drivers_license';
  }

  if (normalizedText.includes('DEPARTMENT OF FOREIGN AFFAIRS') && normalizedText.includes('PASSPORT')) {
    return 'ph_passport';
  }

  if (normalizedText.includes('SOCIAL SECURITY SYSTEM') || normalizedText.includes('SSS')) {
    return 'sss';
  }

  if (normalizedText.includes('BUREAU OF INTERNAL REVENUE') || normalizedText.includes('TIN')) {
    return 'tin';
  }

  if (normalizedText.includes('PHILHEALTH') || normalizedText.includes('PHILIPPINE HEALTH INSURANCE')) {
    return 'philhealth';
  }

  if (normalizedText.includes('COMELEC') || normalizedText.includes('COMMISSION ON ELECTIONS')) {
    return 'voters_id';
  }

  if (normalizedText.includes('PROFESSIONAL REGULATION COMMISSION') || normalizedText.includes('PRC')) {
    return 'prc';
  }

  if (normalizedText.includes('PHLPOST') || normalizedText.includes('POSTAL ID')) {
    return 'postal_id';
  }

  // Generic detection
  if (normalizedText.includes('PASSPORT')) {
    return 'passport';
  }

  if (normalizedText.includes('DRIVER') && normalizedText.includes('LICENSE')) {
    return 'driverLicense';
  }

  return null;
}

function extractDocumentData(text: string, documentType: 'passport' | 'driverLicense') {
  // Remove whitespace and convert to uppercase for consistent matching
  const normalizedText = text.replace(/\s+/g, ' ').toUpperCase();

  if (documentType === 'passport') {
    return {
      documentNumber: extractPassportNumber(normalizedText),
      surname: extractSurname(normalizedText),
      givenNames: extractGivenNames(normalizedText),
      dateOfBirth: extractDate(normalizedText, 'birth'),
      expiryDate: extractDate(normalizedText, 'expiry'),
      nationality: extractNationality(normalizedText),
    };
  } else {
    return {
      licenseNumber: extractLicenseNumber(normalizedText),
      surname: extractSurname(normalizedText),
      givenNames: extractGivenNames(normalizedText),
      dateOfBirth: extractDate(normalizedText, 'birth'),
      expiryDate: extractDate(normalizedText, 'expiry'),
      state: extractState(normalizedText),
    };
  }
}

// Helper functions for data extraction
function extractPassportNumber(text: string): string {
  const match = text.match(/PASSPORT\s*NO\.?\s*([A-Z0-9]{8,9})/);
  return match?.[1] || '';
}

function extractLicenseNumber(text: string): string {
  // Support both international and Philippine formats
  const phMatch = text.match(/(?:LICENSE\s*NO|LIC\s*NO)[.:\s]*([A-Z][0-9]{2}[-\s]?[0-9]{2}[-\s]?[0-9]{6})/);
  if (phMatch) return phMatch[1];

  const match = text.match(/LICENSE\s*NO\.?\s*([A-Z0-9]+)/);
  return match?.[1] || '';
}

function extractSurname(text: string): string {
  const match = text.match(/(?:SURNAME|APELYIDO)\s*([A-Z]+)/);
  return match?.[1] || '';
}

function extractGivenNames(text: string): string {
  const match = text.match(/(?:GIVEN\s*NAMES?|MGA\s*PANGALAN)\s*([A-Z\s]+)/);
  return match?.[1] || '';
}

function extractDate(text: string, type: 'birth' | 'expiry'): string {
  const patterns = type === 'birth'
    ? [
        /(?:DATE\s*OF\s*BIRTH|DOB|PETSA\s*NG\s*KAPANGANAKAN)\s*(\d{2}[-/]\d{2}[-/]\d{4})/,
        /BIRTH\s*(\d{2}\/\d{2}\/\d{4})/
      ]
    : [
        /(?:EXPIRY|EXP|DATE\s*OF\s*EXPIRY|VALID\s*UNTIL)\s*(\d{2}[-/]\d{2}[-/]\d{4})/,
        /EXPIRY\s*(\d{2}\/\d{2}\/\d{4})/
      ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      // Normalize date format to DD/MM/YYYY
      return match[1].replace(/-/g, '/');
    }
  }
  return '';
}

function extractNationality(text: string): string {
  const match = text.match(/(?:NATIONALITY|NASYONALIDAD)\s*([A-Z]{2,3})/);
  return match?.[1] || '';
}

function extractState(text: string): string {
  const match = text.match(/STATE\s*([A-Z]{2,3})/);
  return match?.[1] || '';
}

export async function verifyDocumentAuthenticity(image: File): Promise<{
  isAuthentic: boolean;
  securityFeatures: string[];
  documentType?: DocumentType;
}> {
  // First, extract text to detect document type
  const text = await extractTextFromImage(image);
  const documentType = detectDocumentType(text) || undefined;

  // Analyze security features
  const securityFeatures = await analyzeSecurityFeatures(image, documentType);

  // Check for minimum required security features
  const isAuthentic = securityFeatures.length >= 3;

  return {
    isAuthentic,
    securityFeatures,
    documentType,
  };
}

async function analyzeSecurityFeatures(image: File, documentType?: DocumentType): Promise<string[]> {
  const features: string[] = [];

  // Convert image to ImageData for analysis
  const imageData = await createImageData(image);

  // Check for various security features
  if (await hasUVFeatures(imageData)) {
    features.push('UV_FEATURES');
  }

  if (await hasMicroprint(imageData)) {
    features.push('MICROPRINT');
  }

  if (await hasHologram(imageData)) {
    features.push('HOLOGRAM');
  }

  if (await hasSecurityPattern(imageData)) {
    features.push('SECURITY_PATTERN');
  }

  // Philippine-specific security features
  if (documentType && isPhilippineId(documentType)) {
    if (await hasPhilippineSecurityFeatures(imageData, documentType)) {
      features.push('PH_SECURITY_FEATURES');
    }
  }

  return features;
}

async function createImageData(file: File): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/**
 * DOCUMENT AUTHENTICITY ANALYSIS
 *
 * IMPORTANT: These functions provide basic image analysis to detect obvious fakes.
 * They CANNOT detect:
 * - Real UV features (requires UV light hardware)
 * - Real holograms (requires specific lighting/angles)
 * - Professional forgeries
 *
 * What they CAN detect:
 * - Screen photos (moire patterns, pixel grid)
 * - Low-quality printouts (blur, artifacts)
 * - Obvious image manipulation
 * - Wrong aspect ratios
 * - Digital screenshots
 *
 * For production, consider integrating: Jumio, Onfido, Veriff, or IDnow
 */

// Analyze image sharpness - blurry images are suspicious
async function analyzeSharpness(imageData: ImageData): Promise<{ score: number; isSharp: boolean }> {
  const { width, height, data } = imageData;
  let laplacianVariance = 0;

  // Calculate Laplacian variance (edge detection)
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;

      // Get grayscale value of current and neighboring pixels
      const center = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
      const top = (data[((y - 1) * width + x) * 4] + data[((y - 1) * width + x) * 4 + 1] + data[((y - 1) * width + x) * 4 + 2]) / 3;
      const bottom = (data[((y + 1) * width + x) * 4] + data[((y + 1) * width + x) * 4 + 1] + data[((y + 1) * width + x) * 4 + 2]) / 3;
      const left = (data[(y * width + x - 1) * 4] + data[(y * width + x - 1) * 4 + 1] + data[(y * width + x - 1) * 4 + 2]) / 3;
      const right = (data[(y * width + x + 1) * 4] + data[(y * width + x + 1) * 4 + 1] + data[(y * width + x + 1) * 4 + 2]) / 3;

      // Laplacian = sum of neighbors - 4 * center
      const laplacian = top + bottom + left + right - 4 * center;
      laplacianVariance += laplacian * laplacian;
    }
  }

  laplacianVariance /= ((width - 2) * (height - 2));
  const score = Math.min(100, laplacianVariance / 50);

  // Score > 15 indicates reasonably sharp image
  return { score, isSharp: score > 15 };
}

// Detect moire patterns (indicates photo of screen)
async function detectMoirePattern(imageData: ImageData): Promise<{ hasMoire: boolean; confidence: number }> {
  const { width, height, data } = imageData;

  // Sample the image at regular intervals and look for periodic patterns
  const sampleSize = Math.min(256, Math.min(width, height));
  const frequencies: number[] = [];

  // Analyze horizontal frequency
  for (let y = 0; y < sampleSize; y++) {
    let prevValue = 0;
    let transitions = 0;

    for (let x = 0; x < sampleSize; x++) {
      const idx = (y * width + x) * 4;
      const value = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;

      if (Math.abs(value - prevValue) > 30) {
        transitions++;
      }
      prevValue = value;
    }

    frequencies.push(transitions);
  }

  // Calculate variance of transition frequencies
  const avgFreq = frequencies.reduce((a, b) => a + b, 0) / frequencies.length;
  const variance = frequencies.reduce((sum, f) => sum + Math.pow(f - avgFreq, 2), 0) / frequencies.length;

  // Low variance with high frequency suggests moire pattern
  const hasMoire = avgFreq > 50 && variance < 100;
  const confidence = hasMoire ? Math.min(100, avgFreq / 2) : 0;

  return { hasMoire, confidence };
}

// Check for screen capture artifacts (pixel grid, refresh lines)
async function detectScreenCapture(imageData: ImageData): Promise<{ isScreenCapture: boolean; indicators: string[] }> {
  const indicators: string[] = [];
  const { width, height, data } = imageData;

  // Check for regular pixel grid pattern (screen photos often show this)
  let gridScore = 0;
  const gridSampleSize = Math.min(100, Math.min(width, height));

  for (let y = 2; y < gridSampleSize; y += 3) {
    for (let x = 2; x < gridSampleSize; x += 3) {
      const idx = (y * width + x) * 4;
      const neighbors = [
        ((y - 1) * width + x) * 4,
        ((y + 1) * width + x) * 4,
        (y * width + x - 1) * 4,
        (y * width + x + 1) * 4,
      ];

      let diff = 0;
      for (const nIdx of neighbors) {
        diff += Math.abs(data[idx] - data[nIdx]);
        diff += Math.abs(data[idx + 1] - data[nIdx + 1]);
        diff += Math.abs(data[idx + 2] - data[nIdx + 2]);
      }

      if (diff > 100) gridScore++;
    }
  }

  const gridRatio = gridScore / (Math.pow(gridSampleSize / 3, 2));
  if (gridRatio > 0.3) {
    indicators.push('PIXEL_GRID_DETECTED');
  }

  // Check for uniform noise pattern (digital screenshots have less noise variation)
  const noiseVariance = calculateNoiseVariance(imageData);
  if (noiseVariance < 5) {
    indicators.push('LOW_NOISE_VARIANCE');
  }

  return {
    isScreenCapture: indicators.length >= 1,
    indicators,
  };
}

// Calculate noise variance in the image
function calculateNoiseVariance(imageData: ImageData): number {
  const { width, height, data } = imageData;
  const differences: number[] = [];

  // Sample random pixels and their neighbors
  for (let i = 0; i < 1000; i++) {
    const x = Math.floor(Math.random() * (width - 2)) + 1;
    const y = Math.floor(Math.random() * (height - 2)) + 1;
    const idx = (y * width + x) * 4;
    const neighborIdx = ((y + 1) * width + x) * 4;

    const diff = Math.abs(
      (data[idx] + data[idx + 1] + data[idx + 2]) / 3 -
      (data[neighborIdx] + data[neighborIdx + 1] + data[neighborIdx + 2]) / 3
    );
    differences.push(diff);
  }

  const avg = differences.reduce((a, b) => a + b, 0) / differences.length;
  return differences.reduce((sum, d) => sum + Math.pow(d - avg, 2), 0) / differences.length;
}

// Check aspect ratio matches expected ID card dimensions
function checkAspectRatio(imageData: ImageData, documentType?: DocumentType): { isValid: boolean; ratio: number; expected: string } {
  const { width, height } = imageData;
  const ratio = width / height;

  // Standard ID card ratio is approximately 1.586 (85.6mm x 53.98mm - ISO/IEC 7810 ID-1)
  const ID_CARD_RATIO = 1.586;
  const PASSPORT_RATIO = 0.714; // Passport is taller than wide (125mm x 88mm)

  let expectedRatio = ID_CARD_RATIO;
  let expected = 'ID Card (1.586:1)';

  if (documentType === 'passport' || documentType === 'ph_passport') {
    expectedRatio = PASSPORT_RATIO;
    expected = 'Passport (0.714:1)';
  }

  // Allow 20% tolerance for photo angle/cropping
  const tolerance = 0.2;
  const isValid = Math.abs(ratio - expectedRatio) / expectedRatio < tolerance ||
                  Math.abs((1 / ratio) - expectedRatio) / expectedRatio < tolerance;

  return { isValid, ratio, expected };
}

// Analyze color distribution for document authenticity
async function analyzeColorDistribution(imageData: ImageData): Promise<{ score: number; hasExpectedColors: boolean }> {
  const { data } = imageData;
  const colorBuckets: Record<string, number> = {};
  let totalPixels = data.length / 4;

  // Sample pixels and categorize into color buckets
  for (let i = 0; i < data.length; i += 4 * 10) { // Sample every 10th pixel
    const r = Math.floor(data[i] / 32) * 32;
    const g = Math.floor(data[i + 1] / 32) * 32;
    const b = Math.floor(data[i + 2] / 32) * 32;
    const key = `${r},${g},${b}`;
    colorBuckets[key] = (colorBuckets[key] || 0) + 1;
  }

  // Real IDs typically have:
  // - White/light background areas
  // - Specific brand colors
  // - Not too many dominant colors

  const bucketCount = Object.keys(colorBuckets).length;
  const sampledPixels = totalPixels / 10;

  // Check for presence of white/near-white (common in ID backgrounds)
  let hasWhiteArea = false;
  for (const [color, count] of Object.entries(colorBuckets)) {
    const [r, g, b] = color.split(',').map(Number);
    if (r > 200 && g > 200 && b > 200 && count / sampledPixels > 0.05) {
      hasWhiteArea = true;
      break;
    }
  }

  // Score based on color distribution characteristics
  // Too few colors (< 20) might indicate a simple fake
  // Too many colors (> 200) might indicate a photo of a photo
  const colorScore = bucketCount >= 20 && bucketCount <= 200 ? 50 : 20;
  const whiteScore = hasWhiteArea ? 30 : 0;
  const score = colorScore + whiteScore;

  return {
    score,
    hasExpectedColors: score >= 60,
  };
}

// Detect compression artifacts (heavily compressed images are suspicious)
async function analyzeCompressionArtifacts(imageData: ImageData): Promise<{ score: number; hasArtifacts: boolean }> {
  const { width, height, data } = imageData;
  let blockiness = 0;

  // Check for 8x8 block artifacts (JPEG compression)
  for (let y = 8; y < height - 8; y += 8) {
    for (let x = 8; x < width - 8; x += 8) {
      const idx = (y * width + x) * 4;
      const prevIdx = (y * width + x - 1) * 4;

      // Check horizontal block boundary
      const diff = Math.abs(data[idx] - data[prevIdx]) +
                   Math.abs(data[idx + 1] - data[prevIdx + 1]) +
                   Math.abs(data[idx + 2] - data[prevIdx + 2]);

      if (diff > 30) blockiness++;
    }
  }

  const blockRatio = blockiness / ((width / 8) * (height / 8));
  const hasArtifacts = blockRatio > 0.5;

  return {
    score: hasArtifacts ? 30 : 80,
    hasArtifacts,
  };
}

// Main security feature analysis - REPLACES THE MOCKED FUNCTIONS
async function hasUVFeatures(imageData: ImageData): Promise<boolean> {
  // NOTE: Real UV detection requires UV light hardware
  // We check for image quality indicators that suggest a real physical document

  const sharpness = await analyzeSharpness(imageData);
  const compression = await analyzeCompressionArtifacts(imageData);

  // High quality, sharp image with minimal artifacts suggests real document
  return sharpness.isSharp && compression.score > 50;
}

async function hasMicroprint(imageData: ImageData): Promise<boolean> {
  // NOTE: Real microprint detection requires high-resolution scanning
  // We check for fine detail preservation that suggests high-quality source

  const sharpness = await analyzeSharpness(imageData);

  // Very sharp images (score > 30) may preserve microprint-level detail
  return sharpness.score > 30;
}

async function hasHologram(imageData: ImageData): Promise<boolean> {
  // NOTE: Real hologram detection requires multiple angles/lighting
  // We check for color variation patterns that might indicate holographic elements

  const colors = await analyzeColorDistribution(imageData);
  const moire = await detectMoirePattern(imageData);

  // Real holograms create specific color patterns, not moire
  return colors.hasExpectedColors && !moire.hasMoire;
}

async function hasSecurityPattern(imageData: ImageData): Promise<boolean> {
  // Check for overall document authenticity indicators
  const screen = await detectScreenCapture(imageData);
  const aspectRatio = checkAspectRatio(imageData);
  const compression = await analyzeCompressionArtifacts(imageData);

  // Not a screen capture, valid aspect ratio, acceptable compression
  return !screen.isScreenCapture && aspectRatio.isValid && !compression.hasArtifacts;
}

// Philippine-specific security feature detection
async function hasPhilippineSecurityFeatures(imageData: ImageData, documentType: DocumentType): Promise<boolean> {
  // Run all basic checks
  const sharpness = await analyzeSharpness(imageData);
  const screen = await detectScreenCapture(imageData);
  const aspectRatio = checkAspectRatio(imageData, documentType);
  const colors = await analyzeColorDistribution(imageData);

  // Combine checks - require passing most of them
  const checks = [
    sharpness.isSharp,
    !screen.isScreenCapture,
    aspectRatio.isValid,
    colors.hasExpectedColors,
  ];

  const passCount = checks.filter(Boolean).length;

  // Require at least 3 out of 4 checks to pass
  return passCount >= 3;
}

// Export analysis functions for testing/debugging
export const documentAnalysisUtils = {
  analyzeSharpness,
  detectMoirePattern,
  detectScreenCapture,
  checkAspectRatio,
  analyzeColorDistribution,
  analyzeCompressionArtifacts,
};

// Get list of supported document types
export function getSupportedDocumentTypes(): { id: DocumentType; name: string; country: string }[] {
  const internationalTypes = [
    { id: 'passport' as DocumentType, name: 'International Passport', country: 'International' },
    { id: 'driverLicense' as DocumentType, name: "Driver's License", country: 'International' },
  ];

  const philippineTypes = Object.entries(PHILIPPINE_IDS).map(([key, config]) => ({
    id: key as DocumentType,
    name: config.name,
    country: 'Philippines',
  }));

  return [...philippineTypes, ...internationalTypes];
}
